import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Organization } from "@/types/organization";
import { AlertTriangle, Database, Users, Settings, Package } from "lucide-react";
import { useOrganizationDeletion } from "@/hooks/useOrganizationDeletion";

interface DeleteOrganizationDialogProps {
  organization: Organization | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}

interface OrganizationStats {
  bookings: number;
  members: number;
  settings: number;
  parcelTypes: number;
  eventFlows: number;
  templates: number;
  pendingMemberships: number;
}

export const DeleteOrganizationDialog = ({
  organization,
  open,
  onOpenChange,
  onDeleted,
}: DeleteOrganizationDialogProps) => {
  const [confirmText, setConfirmText] = useState("");
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const { deleteOrganization, fetchOrganizationStats, isDeleting, isFetchingStats } = useOrganizationDeletion();

  useEffect(() => {
    if (open && organization) {
      // Reset confirmation text
      setConfirmText("");
      
      // Fetch stats
      fetchOrganizationStats(organization.id)
        .then(setStats)
        .catch(console.error);
    }
  }, [open, organization]);

  const handleDelete = async () => {
    if (!organization) return;

    const result = await deleteOrganization(organization);
    
    if (result.success) {
      onOpenChange(false);
      onDeleted();
    }
  };

  if (!organization) return null;

  const hasData = stats && (
    stats.bookings > 0 || 
    stats.members > 0 || 
    stats.settings > 0 || 
    stats.parcelTypes > 0 ||
    stats.eventFlows > 0 ||
    stats.templates > 0 ||
    stats.pendingMemberships > 0
  );

  const isConfirmed = hasData 
    ? confirmText === organization.name 
    : true;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Delete Organization
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4 pt-4">
            <div className="space-y-2">
              <p className="text-base font-medium">
                You are about to permanently delete:
              </p>
              <div className="bg-muted p-3 rounded-lg">
                <p className="font-semibold">{organization.display_name || organization.name}</p>
                <p className="text-sm text-muted-foreground">Subdomain: {organization.subdomain}</p>
              </div>
            </div>

            {isFetchingStats ? (
              <div className="flex items-center justify-center py-4">
                <div className="text-sm text-muted-foreground">Loading organization data...</div>
              </div>
            ) : stats ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-destructive">
                  This will permanently delete all related data:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {stats.bookings > 0 && (
                    <div className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <span>{stats.bookings} Bookings</span>
                    </div>
                  )}
                  {stats.members > 0 && (
                    <div className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span>{stats.members} Members</span>
                    </div>
                  )}
                  {stats.parcelTypes > 0 && (
                    <div className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                      <Package className="w-4 h-4 text-muted-foreground" />
                      <span>{stats.parcelTypes} Parcel Types</span>
                    </div>
                  )}
                  {stats.settings > 0 && (
                    <div className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                      <Settings className="w-4 h-4 text-muted-foreground" />
                      <span>{stats.settings} Settings</span>
                    </div>
                  )}
                  {stats.eventFlows > 0 && (
                    <div className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <span>{stats.eventFlows} Event Flows</span>
                    </div>
                  )}
                  {stats.templates > 0 && (
                    <div className="flex items-center gap-2 bg-muted/50 p-2 rounded">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <span>{stats.templates} Templates</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {hasData && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="confirm-text" className="text-sm font-medium">
                  Type <span className="font-mono font-bold">{organization.name}</span> to confirm:
                </Label>
                <Input
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Enter organization name"
                  className="font-mono"
                  disabled={isDeleting}
                />
              </div>
            )}

            <p className="text-sm text-destructive font-medium">
              ⚠️ This action cannot be undone!
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={!isConfirmed || isDeleting || isFetchingStats}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting..." : "Delete Organization"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
